/*  SocialFeed.jsx — shared "FB × LinkedIn × IG" primitives.
 *
 *  Three reusable pieces for public profile pages:
 *    · AnnouncementWall  — Facebook-style post feed with reactions
 *                          (👏 clap · 🎬 rolling · 🎉 celebrate).
 *                          Owner sees an inline composer.
 *    · StudioMomentsStrip — Instagram-style photo strip capped at 3
 *                           posts / 24h. Photos age out after 30 days.
 *    · CouncilRoster      — LinkedIn-style office-bearer grid for unions.
 *
 *  Every component is theme-aware (`t` prop) so it drops seamlessly
 *  into agency-cream, production-neon, or union palettes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import {
  Camera, Plus, Trash2, Image as ImageIcon, Send, X,
  Hand, Video, PartyPopper, Award, Building2,
} from "lucide-react";


const REACTIONS = [
  { key: "clap",      icon: Hand,        label: "Clap"      },
  { key: "rolling",   icon: Video,       label: "Rolling"   },
  { key: "celebrate", icon: PartyPopper, label: "Celebrate" },
];


function _absoluteUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  return `${API.replace(/\/api$/, "")}${url}`;
}


/* ═══════════════════════════════════════════════════════════════════
 *   AnnouncementWall — FB-style feed with composer + reactions.
 * ═══════════════════════════════════════════════════════════════════ */
export function AnnouncementWall({
  isAdmin, t,
  listUrl,       // GET public list, e.g. /public/orgs/{handle}/announcements
  createUrl,     // POST auth create (multipart), e.g. /orgs/mine/announcements
  deleteUrl,     // (aid) => DELETE URL
  reactUrl,      // (aid) => POST reaction URL
  photoUrlFor,   // (post) => absolute image URL
  title = "The Wall",
  eyebrow = "Announcements",
  emptyLabel = "No announcements yet.",
  testidPrefix = "wall",
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setPosts(data?.announcements || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load posts");
    } finally { setLoading(false); }
  }, [listUrl]);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (aid) => {
    if (!window.confirm("Remove this post from the wall?")) return;
    try {
      await api.delete(deleteUrl(aid));
      toast.success("Post removed");
      setPosts((cur) => cur.filter((p) => p.id !== aid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin && !loading && posts.length === 0) return null;

  return (
    <section className="mt-12" data-testid={`${testidPrefix}-section`}>
      <div className="mb-3 flex items-baseline gap-4">
        <div className="text-[10px] tracking-[0.3em] uppercase"
             style={{ color: t.muted || "rgba(255,255,255,0.5)" }}>
          {eyebrow}
        </div>
      </div>
      <h2 className="font-display text-3xl uppercase tracking-tight mb-6"
          style={{ color: t.ink }}>
        {title}
      </h2>

      {isAdmin && (
        <WallComposer t={t} createUrl={createUrl} onPosted={load}
                      testidPrefix={testidPrefix} />
      )}

      <div className="mt-6 space-y-4">
        {loading && (
          <div className="text-sm" style={{ color: t.muted }}>Loading…</div>
        )}
        {!loading && posts.length === 0 && (
          <div className="rounded-2xl p-8 text-center"
               style={{ background: t.surface,
                        border: `1px solid ${t.line}` }}
               data-testid={`${testidPrefix}-empty`}>
            <div className="text-sm" style={{ color: t.muted }}>{emptyLabel}</div>
          </div>
        )}
        {posts.map((p) => (
          <WallPost key={p.id} post={p} t={t}
                    isAdmin={isAdmin}
                    onDelete={() => onDelete(p.id)}
                    reactUrl={reactUrl}
                    photoUrl={photoUrlFor ? photoUrlFor(p) : _absoluteUrl(p.photo_url)}
                    testidPrefix={testidPrefix} />
        ))}
      </div>
    </section>
  );
}


function WallComposer({ t, createUrl, onPosted, testidPrefix }) {
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!body.trim()) { toast.error("Write something to post"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      fd.append("kind", "text");
      if (file) fd.append("file", file);
      await api.post(createUrl, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Posted to the wall");
      setBody(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-4"
         style={{ background: t.surface, border: `1px solid ${t.line}` }}
         data-testid={`${testidPrefix}-composer`}>
      <textarea
        rows={3}
        value={body}
        maxLength={1200}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Share a wrap update, casting call, or milestone…"
        className="w-full resize-none rounded-lg bg-transparent p-3 text-sm outline-none placeholder:opacity-60"
        style={{ color: t.ink,
                 border: `1px solid ${t.line}`,
                 background: t.inputBg || "transparent" }}
        data-testid={`${testidPrefix}-composer-input`}
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 text-xs"
               style={{ color: t.muted,
                        border: `1px solid ${t.line}` }}
               data-testid={`${testidPrefix}-composer-attach`}>
          <ImageIcon size={14} /> {file ? file.name.slice(0, 32) : "Attach photo"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="flex items-center gap-2">
          {file && (
            <button type="button" className="text-xs underline"
                    style={{ color: t.muted }}
                    onClick={() => {
                      setFile(null);
                      if (fileRef.current) fileRef.current.value = "";
                    }}>
              Clear
            </button>
          )}
          <span className="text-[11px]" style={{ color: t.muted }}>
            {body.length}/1200
          </span>
          <button type="button"
                  onClick={submit}
                  disabled={busy || !body.trim()}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-wider transition disabled:opacity-40"
                  style={{ background: t.accent,
                           color: t.ctaText || "#fff" }}
                  data-testid={`${testidPrefix}-composer-submit`}>
            <Send size={14} /> Post
          </button>
        </div>
      </div>
    </div>
  );
}


function WallPost({ post, t, isAdmin, onDelete, reactUrl, photoUrl, testidPrefix }) {
  const [reactions, setReactions] = useState(post.reactions || {});
  const [yourReaction, setYourReaction] = useState(null);
  const [busy, setBusy] = useState(false);

  const react = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post(reactUrl(post.id), { reaction: key });
      setReactions(data.reactions || {});
      setYourReaction(data.your_reaction || null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign in to react");
    } finally { setBusy(false); }
  };

  return (
    <article className="rounded-2xl overflow-hidden"
             style={{ background: t.surface,
                      border: `1px solid ${t.line}` }}
             data-testid={`${testidPrefix}-post-${post.id}`}>
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.24em] uppercase"
                 style={{ color: t.muted }}>
              {post.author_name}{post.author_role ? ` · ${post.author_role}` : ""}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: t.muted }}>
              {new Date(post.created_at).toLocaleString()}
            </div>
          </div>
          {isAdmin && (
            <button type="button" onClick={onDelete}
                    className="rounded-full p-1.5 opacity-60 hover:opacity-100 transition"
                    style={{ border: `1px solid ${t.line}`, color: t.muted }}
                    data-testid={`${testidPrefix}-post-delete-${post.id}`}>
              <Trash2 size={13} />
            </button>
          )}
        </div>
        <div className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed"
             style={{ color: t.ink }}>
          {post.body}
        </div>
      </div>
      {photoUrl && (
        <div className="border-t" style={{ borderColor: t.line }}>
          <img src={photoUrl} alt=""
               className="w-full max-h-96 object-cover"
               onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      )}
      <div className="flex items-center gap-2 px-4 py-3 border-t"
           style={{ borderColor: t.line }}>
        {REACTIONS.map(({ key, icon: Icon, label }) => {
          const active = yourReaction === key;
          return (
            <button key={key} type="button"
                    onClick={() => react(key)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition disabled:opacity-40"
                    style={{
                      color: active ? (t.ctaText || t.ink) : t.muted,
                      background: active ? t.accent : "transparent",
                      border: `1px solid ${active ? t.accent : t.line}`,
                    }}
                    data-testid={`${testidPrefix}-react-${key}-${post.id}`}>
              <Icon size={13} /> {label}
              {reactions[key] > 0 && (
                <span className="ml-1 font-semibold">{reactions[key]}</span>
              )}
            </button>
          );
        })}
      </div>
    </article>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *   StudioMomentsStrip — IG-style moments carousel.
 * ═══════════════════════════════════════════════════════════════════ */
export function StudioMomentsStrip({
  isAdmin, t,
  publicListUrl,   // GET /public/orgs/{handle}/moments
  ownerListUrl,    // GET /orgs/mine/moments (with quota)
  createUrl,       // POST /orgs/mine/moments
  deleteUrl,       // (mid) => DELETE URL
  photoUrlFor,     // (m)   => absolute URL
  title = "Studio Moments",
  eyebrow = "Now Shooting",
  emptyLabel = "No moments posted yet.",
  testidPrefix = "moments",
}) {
  const [moments, setMoments] = useState([]);
  const [quota, setQuota]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const { data } = await api.get(ownerListUrl);
        setMoments(data?.moments || []);
        setQuota(data?.quota || null);
      } else {
        const { data } = await api.get(publicListUrl);
        setMoments(data?.moments || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load moments");
    } finally { setLoading(false); }
  }, [isAdmin, ownerListUrl, publicListUrl]);
  useEffect(() => { load(); }, [load]);

  const remove = async (mid) => {
    if (!window.confirm("Delete this moment?")) return;
    try {
      await api.delete(deleteUrl(mid));
      setMoments((cur) => cur.filter((m) => m.id !== mid));
      toast.success("Moment removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin && !loading && moments.length === 0) return null;

  return (
    <section className="mt-12" data-testid={`${testidPrefix}-section`}>
      <div className="mb-3 flex items-baseline gap-4">
        <div className="text-[10px] tracking-[0.3em] uppercase"
             style={{ color: t.muted }}>
          {eyebrow}
        </div>
      </div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-3xl uppercase tracking-tight"
            style={{ color: t.ink }}>
          {title}
        </h2>
        {isAdmin && quota && (
          <div className="text-[11px]" style={{ color: t.muted }}>
            {quota.remaining} of {quota.daily_max} posts left today · rolling 24h
          </div>
        )}
      </div>

      {isAdmin && (
        <MomentComposer t={t} createUrl={createUrl} onPosted={load}
                        canPost={!quota || quota.remaining > 0}
                        testidPrefix={testidPrefix} />
      )}

      <div className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4"
           data-testid={`${testidPrefix}-strip`}>
        {loading && (
          <div className="text-sm" style={{ color: t.muted }}>Loading…</div>
        )}
        {!loading && moments.length === 0 && (
          <div className="min-w-full rounded-2xl p-8 text-center"
               style={{ background: t.surface,
                        border: `1px solid ${t.line}` }}
               data-testid={`${testidPrefix}-empty`}>
            <div className="text-sm" style={{ color: t.muted }}>{emptyLabel}</div>
          </div>
        )}
        {moments.map((m) => (
          <div key={m.id}
               className="relative min-w-[260px] max-w-[260px] snap-start rounded-2xl overflow-hidden"
               style={{ background: t.surface,
                        border: `1px solid ${t.line}` }}
               data-testid={`${testidPrefix}-card-${m.id}`}>
            <div className="aspect-square w-full overflow-hidden"
                 style={{ background: t.chipBg }}>
              <img src={photoUrlFor ? photoUrlFor(m) : _absoluteUrl(m.photo_url)}
                   alt="" loading="lazy"
                   className="h-full w-full object-cover"
                   onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="p-3">
              {m.caption && (
                <div className="text-[13px] leading-snug"
                     style={{ color: t.ink }}>
                  {m.caption}
                </div>
              )}
              {m.location && (
                <div className="mt-1 text-[10px] tracking-wider uppercase"
                     style={{ color: t.muted }}>
                  {m.location}
                </div>
              )}
              <div className="mt-2 text-[10px]" style={{ color: t.muted }}>
                {new Date(m.created_at).toLocaleDateString()}
              </div>
            </div>
            {isAdmin && (
              <button type="button" onClick={() => remove(m.id)}
                      className="absolute right-2 top-2 rounded-full p-1.5"
                      style={{ background: "rgba(0,0,0,0.55)",
                               color: "#fff" }}
                      data-testid={`${testidPrefix}-delete-${m.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function MomentComposer({ t, createUrl, onPosted, canPost, testidPrefix }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const preview = file ? URL.createObjectURL(file) : "";

  const submit = async () => {
    if (!file) { toast.error("Pick a photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption.trim());
      fd.append("location", location.trim());
      await api.post(createUrl, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Moment posted");
      setFile(null); setCaption(""); setLocation("");
      if (fileRef.current) fileRef.current.value = "";
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-4"
         style={{ background: t.surface, border: `1px solid ${t.line}` }}
         data-testid={`${testidPrefix}-composer`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <label className="relative flex h-24 w-24 shrink-0 cursor-pointer items-center justify-center rounded-xl overflow-hidden"
               style={{ background: t.chipBg, border: `1px dashed ${t.line}` }}
               data-testid={`${testidPrefix}-composer-pick`}>
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <Camera size={22} style={{ color: t.muted }} />
          )}
          <input ref={fileRef} type="file" accept="image/*"
                 capture="environment" className="hidden"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="flex-1 space-y-2">
          <input type="text" value={caption} maxLength={240}
                 onChange={(e) => setCaption(e.target.value)}
                 placeholder="Caption (240 chars)"
                 className="w-full rounded-lg p-2 text-sm outline-none"
                 style={{ color: t.ink, background: t.inputBg || "transparent",
                          border: `1px solid ${t.line}` }}
                 data-testid={`${testidPrefix}-composer-caption`} />
          <input type="text" value={location} maxLength={120}
                 onChange={(e) => setLocation(e.target.value)}
                 placeholder="Location · e.g. Mumbai · Set 3"
                 className="w-full rounded-lg p-2 text-sm outline-none"
                 style={{ color: t.ink, background: t.inputBg || "transparent",
                          border: `1px solid ${t.line}` }}
                 data-testid={`${testidPrefix}-composer-location`} />
          <div className="flex justify-end">
            <button type="button"
                    onClick={submit}
                    disabled={busy || !file || !canPost}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-wider transition disabled:opacity-40"
                    style={{ background: t.accent,
                             color: t.ctaText || "#fff" }}
                    data-testid={`${testidPrefix}-composer-submit`}>
              <Plus size={13} /> {canPost ? "Post moment" : "Daily limit hit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *   CouncilRoster — office bearers grid (union profile).
 * ═══════════════════════════════════════════════════════════════════ */
export function CouncilRoster({
  isAdmin, t,
  listUrl,       // GET /public/associations/{id}/council
  createUrl,     // POST /associations/{id}/council (multipart)
  deleteUrl,     // (cid) => DELETE URL
  photoUrlFor,   // (member) => absolute URL
  title = "Office Bearers",
  eyebrow = "The Council",
  testidPrefix = "council",
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setMembers(data?.council || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load council");
    } finally { setLoading(false); }
  }, [listUrl]);
  useEffect(() => { load(); }, [load]);

  const remove = async (cid) => {
    if (!window.confirm("Remove this council member?")) return;
    try {
      await api.delete(deleteUrl(cid));
      setMembers((cur) => cur.filter((m) => m.id !== cid));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin && !loading && members.length === 0) return null;

  return (
    <section className="mt-12" data-testid={`${testidPrefix}-section`}>
      <div className="mb-3 flex items-baseline gap-4">
        <div className="text-[10px] tracking-[0.3em] uppercase"
             style={{ color: t.muted }}>
          {eyebrow}
        </div>
      </div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-3xl uppercase tracking-tight"
            style={{ color: t.ink }}>
          {title}
        </h2>
        {isAdmin && !adding && (
          <button type="button" onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs uppercase tracking-wider"
                  style={{ background: t.accent, color: t.ctaText || "#fff" }}
                  data-testid={`${testidPrefix}-add-btn`}>
            <Plus size={13} /> Add member
          </button>
        )}
      </div>

      {adding && (
        <CouncilComposer t={t} createUrl={createUrl}
                          onPosted={() => { setAdding(false); load(); }}
                          onCancel={() => setAdding(false)}
                          testidPrefix={testidPrefix} />
      )}

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {loading && (
          <div className="text-sm" style={{ color: t.muted }}>Loading…</div>
        )}
        {!loading && members.length === 0 && !adding && (
          <div className="col-span-full rounded-2xl p-8 text-center"
               style={{ background: t.surface, border: `1px solid ${t.line}` }}>
            <div className="text-sm" style={{ color: t.muted }}>
              No office bearers listed yet.
            </div>
          </div>
        )}
        {members.map((m) => (
          <div key={m.id}
               className="relative rounded-2xl overflow-hidden"
               style={{ background: t.surface, border: `1px solid ${t.line}` }}
               data-testid={`${testidPrefix}-card-${m.id}`}>
            <div className="aspect-[3/4] w-full overflow-hidden"
                 style={{ background: t.chipBg }}>
              {m.photo_url ? (
                <img src={photoUrlFor ? photoUrlFor(m) : _absoluteUrl(m.photo_url)}
                     alt="" loading="lazy"
                     className="h-full w-full object-cover"
                     onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Award size={36} style={{ color: t.muted }} />
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="text-[10px] tracking-[0.22em] uppercase"
                   style={{ color: t.accent }}>
                {m.position}
              </div>
              <div className="mt-1 font-display text-lg uppercase tracking-tight leading-tight"
                   style={{ color: t.ink }}>
                {m.name}
              </div>
              {m.tenure && (
                <div className="mt-1 text-[11px]" style={{ color: t.muted }}>
                  {m.tenure}
                </div>
              )}
            </div>
            {isAdmin && (
              <button type="button" onClick={() => remove(m.id)}
                      className="absolute right-2 top-2 rounded-full p-1.5"
                      style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }}
                      data-testid={`${testidPrefix}-delete-${m.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function CouncilComposer({ t, createUrl, onPosted, onCancel, testidPrefix }) {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [tenure, setTenure] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!name.trim() || !position.trim()) {
      toast.error("Name and position are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("position", position.trim());
      fd.append("tenure", tenure.trim());
      if (file) fd.append("file", file);
      await api.post(createUrl, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Council member added");
      setName(""); setPosition(""); setTenure(""); setFile(null);
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-4"
         style={{ background: t.surface, border: `1px solid ${t.line}` }}
         data-testid={`${testidPrefix}-composer`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <label className="relative flex h-24 w-24 shrink-0 cursor-pointer items-center justify-center rounded-xl overflow-hidden"
               style={{ background: t.chipBg, border: `1px dashed ${t.line}` }}>
          {file ? (
            <img src={URL.createObjectURL(file)} alt=""
                 className="h-full w-full object-cover" />
          ) : (
            <Camera size={22} style={{ color: t.muted }} />
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input type="text" value={name} maxLength={120}
                 onChange={(e) => setName(e.target.value)}
                 placeholder="Full name"
                 className="rounded-lg p-2 text-sm outline-none"
                 style={{ color: t.ink, background: t.inputBg || "transparent",
                          border: `1px solid ${t.line}` }}
                 data-testid={`${testidPrefix}-composer-name`} />
          <input type="text" value={position} maxLength={120}
                 onChange={(e) => setPosition(e.target.value)}
                 placeholder="Position · e.g. President"
                 className="rounded-lg p-2 text-sm outline-none"
                 style={{ color: t.ink, background: t.inputBg || "transparent",
                          border: `1px solid ${t.line}` }}
                 data-testid={`${testidPrefix}-composer-position`} />
          <input type="text" value={tenure} maxLength={80}
                 onChange={(e) => setTenure(e.target.value)}
                 placeholder="Tenure · e.g. 2023–2026"
                 className="rounded-lg p-2 text-sm outline-none sm:col-span-2"
                 style={{ color: t.ink, background: t.inputBg || "transparent",
                          border: `1px solid ${t.line}` }}
                 data-testid={`${testidPrefix}-composer-tenure`} />
          <div className="sm:col-span-2 flex items-center justify-end gap-2">
            <button type="button" onClick={onCancel}
                    className="rounded-full px-3 py-1.5 text-xs"
                    style={{ color: t.muted, border: `1px solid ${t.line}` }}>
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-wider disabled:opacity-40"
                    style={{ background: t.accent, color: t.ctaText || "#fff" }}
                    data-testid={`${testidPrefix}-composer-submit`}>
              <Plus size={13} /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
